/**
 * Data layer for the settings surface (ARCHITECTURE §7, §8, §10;
 * ARCHITECTURE-LAYERS.md §4.1): `hc<AppType>` fetchers and `queryOptions`
 * factories. Mutation hooks live in `./hooks` — this file is reads + the bare
 * fetchers those hooks wrap.
 */
import { queryOptions } from "@tanstack/react-query";
import type {
  AuthQrIssueResponse,
  CredentialKind,
  GetAgentToolsResponse,
  GetAiAliasesResponse,
  GetAiModelsResponse,
  GetSettingsResponse,
  ListAccountsResponse,
  ListCredentialsResponse,
  PutAgentTools,
  PutAiAliases,
  PutCredential,
  SyncAccountsResponse,
  TestCredentialResponse,
  UpdateSettings,
} from "@richrich/contracts";
import { api, unwrap } from "../../lib/api";
import { settingsQuery } from "../../lib/query";
import { keys } from "../../lib/queryKeys";

/** Masked credential list (`GET /api/settings/credentials`). */
export const credentialsQuery = queryOptions({
  queryKey: keys.settings.credentials(),
  queryFn: async (): Promise<ListCredentialsResponse> =>
    unwrap<ListCredentialsResponse>(await api.settings.credentials.$get()),
  staleTime: 30_000,
});

/**
 * Cached broker accounts (`GET /api/accounts`). Settings owns this domain (it
 * also owns {@link useSyncAccounts}, the only write to the cache entry), so
 * this is the ONE definition — dashboard and strategies re-export it from here
 * rather than declaring their own `queryOptions` (ARCHITECTURE-LAYERS.md §4.1;
 * previously three near-identical copies drifted on the key string — see
 * queryKeys.ts for the `['accounts']` vs `['accounts','list']` bug that fixed).
 */
export const accountsQuery = queryOptions({
  queryKey: keys.accounts.list(),
  queryFn: async (): Promise<ListAccountsResponse> =>
    unwrap<ListAccountsResponse>(await api.accounts.$get()),
  staleTime: 60_000,
});

/** `PUT /api/settings`. Internal `mutationFn` for {@link useUpdateSettings} (`./hooks`). */
export async function putSettings(
  body: UpdateSettings & { confirm?: boolean },
): Promise<GetSettingsResponse> {
  return unwrap<GetSettingsResponse>(await api.settings.$put({ json: body }));
}

/** `PUT /api/settings/credentials/:kind` — internal `mutationFn` for {@link usePutCredential} (`./hooks`). */
export async function putCredential(kind: CredentialKind, body: PutCredential): Promise<unknown> {
  return unwrap(await api.settings.credentials[":kind"].$put({ param: { kind }, json: body }));
}

/** `DELETE /api/settings/credentials/:kind` — internal `mutationFn` for {@link useDeleteCredential} (`./hooks`). */
export async function deleteCredential(kind: CredentialKind): Promise<unknown> {
  return unwrap(await api.settings.credentials[":kind"].$delete({ param: { kind } }));
}

/** `POST /api/settings/credentials/:kind/test` — internal `mutationFn` for {@link useTestCredential} (`./hooks`). */
export async function testCredential(kind: CredentialKind): Promise<TestCredentialResponse> {
  return unwrap<TestCredentialResponse>(
    await api.settings.credentials[":kind"].test.$post({ param: { kind } }),
  );
}

/** Effective + default per-role model aliases (`GET /api/settings/ai-aliases`). */
export const aiAliasesQuery = queryOptions({
  queryKey: keys.settings.aiAliases(),
  queryFn: async (): Promise<GetAiAliasesResponse> =>
    unwrap<GetAiAliasesResponse>(await api.settings["ai-aliases"].$get()),
  staleTime: 30_000,
});

/**
 * Models available per registered provider (`GET /api/settings/ai-models`),
 * driving the guided alias picker. Cached 5 min client-side to match the server's
 * KV TTL on the custom `/models` fetch.
 */
export const aiModelsQuery = queryOptions({
  queryKey: keys.settings.aiModels(),
  queryFn: async (): Promise<GetAiModelsResponse> =>
    unwrap<GetAiModelsResponse>(await api.settings["ai-models"].$get()),
  staleTime: 5 * 60_000,
});

/** `PUT /api/settings/ai-aliases` — internal `mutationFn` for {@link usePutAiAliases} (`./hooks`). */
export async function putAiAliases(body: PutAiAliases): Promise<GetAiAliasesResponse> {
  return unwrap<GetAiAliasesResponse>(await api.settings["ai-aliases"].$put({ json: body }));
}

/** Per-agent tool catalog + enabled state (`GET /api/settings/agent-tools`). */
export const agentToolsQuery = queryOptions({
  queryKey: keys.settings.agentTools(),
  queryFn: async (): Promise<GetAgentToolsResponse> =>
    unwrap<GetAgentToolsResponse>(await api.settings["agent-tools"].$get()),
  staleTime: 30_000,
});

/** `PUT /api/settings/agent-tools` — internal `mutationFn` for {@link usePutAgentTools} (`./hooks`). */
export async function putAgentTools(body: PutAgentTools): Promise<GetAgentToolsResponse> {
  return unwrap<GetAgentToolsResponse>(await api.settings["agent-tools"].$put({ json: body }));
}

/** `POST /api/accounts/sync` — internal `mutationFn` for {@link useSyncAccounts} (`./hooks`). */
export async function syncAccounts(): Promise<SyncAccountsResponse> {
  return unwrap<SyncAccountsResponse>(await api.accounts.sync.$post());
}

/** `POST /api/auth/qr/issue` — internal `mutationFn` for {@link useIssueQrLogin} (`./hooks`). */
export async function issueQrLogin(): Promise<AuthQrIssueResponse> {
  return unwrap<AuthQrIssueResponse>(await api.auth.qr.issue.$post());
}

export { settingsQuery };
